// src/pages/ia-paca/index.tsx
//
// PAGE PILIER /ia-paca/
//
// Positionnement : "Équipe qui opère des systèmes IA en production
// depuis 2022, qui recrute aussi les profils IA pour les boîtes de
// la région." Anti-cabinet RH générique. Anti-bullshit.
//
// SEO : mot-clé principal "recrutement IA PACA", longue traîne sur
// métiers IA + villes. JSON-LD Organization + ProfessionalService.
//
// Conversion : 3 niveaux de CTA (téléchargement baromètre / call 15min
// / brief recrutement complet).

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaHero from '../../components/ia-paca/IaPacaHero';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import IaSalaryTable from '../../components/ia-paca/IaSalaryTable';
import IaStackBadges from '../../components/ia-paca/IaStackBadges';
import {
  Wrench,
  Target,
  MapPin,
  ArrowRight,
  Code2,
  GitBranch,
  Database,
  Zap,
  Brain,
  Workflow,
  BarChart3,
  Cpu,
  Bot,
  Layers,
  Sparkles,
  FileText,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

const METIERS = [
  {
    slug: 'ai-engineer',
    label: 'AI Engineer',
    pitch: 'Construit des produits IA en prod (RAG, agents, fine-tuning).',
    icon: Brain,
    salary: '55 – 105 k€',
  },
  {
    slug: 'ml-engineer',
    label: 'Machine Learning Engineer',
    pitch: 'Pipeline ML de bout en bout, industrialisation.',
    icon: GitBranch,
    salary: '52 – 100 k€',
  },
  {
    slug: 'data-scientist',
    label: 'Data Scientist',
    pitch: 'Modèles prédictifs, statistique, exploration data.',
    icon: BarChart3,
    salary: '48 – 90 k€',
  },
  {
    slug: 'mlops-engineer',
    label: 'MLOps Engineer',
    pitch: 'Infra GPU, monitoring, déploiement de modèles.',
    icon: Cpu,
    salary: '55 – 110 k€',
  },
  {
    slug: 'ai-automation-specialist',
    label: 'AI Automation Specialist',
    pitch: 'n8n / Make / Zapier + LLM, workflows métier.',
    icon: Workflow,
    salary: '48 – 85 k€',
  },
  {
    slug: 'prompt-engineer',
    label: 'Prompt Engineer',
    pitch: 'Optimisation LLM, eval, guardrails.',
    icon: FileText,
    salary: '50 – 90 k€',
  },
  {
    slug: 'ai-product-manager',
    label: 'AI Product Manager',
    pitch: 'Pilote produit IA, comprend tech + business.',
    icon: Target,
    salary: '62 – 120 k€',
  },
  {
    slug: 'data-engineer-ia',
    label: 'Data Engineer IA',
    pitch: 'Pipelines data qui alimentent les modèles.',
    icon: Database,
    salary: '50 – 95 k€',
  },
  {
    slug: 'architecte-ia',
    label: 'Architecte IA',
    pitch: 'Design système IA scalable, vision long terme.',
    icon: Layers,
    salary: '95 – 160 k€',
  },
  {
    slug: 'consultant-ia',
    label: 'Consultant IA',
    pitch: 'Accompagne la transfo IA des entreprises.',
    icon: Sparkles,
    salary: '55 – 120 k€',
  },
  {
    slug: 'developpeur-llm',
    label: 'Développeur LLM',
    pitch: 'Fine-tune, sert, scale les modèles.',
    icon: Code2,
    salary: '55 – 110 k€',
  },
  {
    slug: 'expert-ia-generative',
    label: 'Expert IA générative',
    pitch: 'GenAI applicative, création de produits.',
    icon: Zap,
    salary: '60 – 130 k€',
  },
  {
    slug: 'agentic-ai-engineer',
    label: 'Agentic AI Engineer',
    pitch: 'Agents autonomes, tool calling, multi-step.',
    icon: Bot,
    salary: '62 – 130 k€',
    isNew: true,
  },
];

const VILLES = [
  {
    slug: 'marseille',
    label: 'Marseille',
    pitch: 'Scale-ups, CMA CGM × Mistral, PME en transfo.',
    tag: 'Hub principal',
  },
  {
    slug: 'sophia-antipolis',
    label: 'Sophia Antipolis',
    pitch: '2 500 entreprises tech · Pôle Alpha · 3IA · EURECOM.',
    tag: 'Tech historique',
  },
  {
    slug: 'aix-en-provence',
    label: 'Aix-en-Provence',
    pitch: 'Technopôle Arbois, Pôle Pégase, CEA Cadarache, ETI industrielles.',
    tag: 'R&D + industrie',
  },
  {
    slug: 'nice',
    label: 'Nice',
    pitch: 'IMREDD, MedTech, gaming, FinTech, smart city.',
    tag: 'Côte d\'Azur',
  },
  {
    slug: 'toulon',
    label: 'Toulon',
    pitch: 'Naval défense + DGA → IA sécurité.',
    tag: 'À venir',
  },
];

export default function IaPacaPilierPage() {
  // JSON-LD Organization + ProfessionalService (SEO local + entité)
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Recruteurs.io — Recrutement IA PACA',
    description:
      "Cabinet de recrutement IA en Provence-Alpes-Côte d'Azur. Équipe qui opère des systèmes d'intelligence artificielle en production depuis 2022.",
    url: 'https://recruteurs.io/ia-paca',
    logo: 'https://recruteurs.io/logo.png',
    areaServed: [
      { '@type': 'City', name: 'Marseille' },
      { '@type': 'City', name: 'Sophia Antipolis' },
      { '@type': 'City', name: 'Aix-en-Provence' },
      { '@type': 'City', name: 'Nice' },
      { '@type': 'City', name: 'Toulon' },
      { '@type': 'AdministrativeArea', name: "Provence-Alpes-Côte d'Azur" },
    ],
    serviceType: [
      'Recrutement AI Engineer',
      'Recrutement Machine Learning Engineer',
      'Recrutement MLOps Engineer',
      'Recrutement Data Scientist',
      'Recrutement Prompt Engineer',
      'Recrutement Consultant IA',
      'Recrutement Agentic AI Engineer',
    ],
  };

  return (
    <Layout
      title="Recrutement IA en PACA — opérateurs IA qui recrutent | recruteurs.io"
      description="Cabinet de recrutement IA basé en PACA. Nous opérons des systèmes d'IA en production depuis 2022 : on sait reconnaître un vrai AI Engineer d'un développeur qui a mis du GPT dans son CV. Marseille · Sophia · Aix · Nice."
      keywords="recrutement IA PACA, recrutement AI Engineer Marseille, recrutement MLOps Sophia Antipolis, cabinet recrutement intelligence artificielle, recruter data scientist PACA, recrutement IA Aix-en-Provence"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* HERO */}
      <IaPacaHero
        title="Recruter des profils IA en PACA,"
        highlight="sans tomber dans le buzzword."
        subtitle="On opère des systèmes d'IA en production depuis 2022 — agents, RAG, automatisations, pipelines LLM. Cette pratique terrain nous permet de reconnaître un vrai AI Engineer d'un développeur qui a juste mis du GPT-4 dans son CV. Cabinet basé en PACA, recrutement Marseille, Sophia, Aix, Nice."
        badges={['Marseille', 'Sophia Antipolis', 'Aix-en-Provence', 'Nice', 'Toulon']}
      />

      {/* POURQUOI NOUS — 3 piliers de différenciation */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Pourquoi nous
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Le seul cabinet qui parle votre langage technique.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              99% des cabinets RH se sont mis à l'IA parce que c'est tendance.
              Nous, on a commencé à opérer des systèmes IA avant même de
              recruter pour les autres. La différence se voit dès le brief.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
            <Pillar
              Icon={Wrench}
              title="On opère l'IA"
              body="Nous concevons et maintenons en production : agents conversationnels, workflows d'automatisation, pipelines RAG, fine-tuning de modèles. Pas de théorie. On sait ce qui casse en prod, ce qui coûte cher, ce qui scale."
              tag="Pratique terrain"
            />
            <Pillar
              Icon={Target}
              title="On distingue les vrais experts"
              body="Un MLOps Engineer ≠ Data Engineer ≠ AI Engineer ≠ Prompt Engineer. La majorité des cabinets confondent ces métiers. Nous testons techniquement nos candidats avec des cas réels — RAG, eval, prompt versioning, debugging d'agent."
              tag="Filtrage technique"
            />
            <Pillar
              Icon={MapPin}
              title="On connaît PACA"
              body="Marseille, Sophia Antipolis, Aix, Nice, Toulon. Pôle Alpha, La Coque, French Tech Aix-Marseille, 3IA Côte d'Azur. Réseau de profils locaux ou prêts à relocaliser dans le sud. Pas de sourcing depuis Paris à l'aveugle."
              tag="Ancrage local"
            />
          </div>
        </div>
      </section>

      {/* STACK IA — preuve technique */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
                La stack IA qu'on opère
              </div>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                On parle le langage des CTO et Head of Data.
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Voici les outils, frameworks et providers qu'on utilise au
                quotidien pour nos propres projets IA — et donc ce qu'on
                évalue en entretien technique chez vos candidats.
              </p>
              <p className="mt-3 text-sm text-slate-500">
                <strong>Aucun cabinet RH classique ne sait écrire cette
                liste sans copier-coller.</strong> Nous oui : c'est notre
                quotidien.
              </p>
              <div className="mt-6 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-violet-700" />
                  <p className="text-sm text-slate-700">
                    <strong>Concrètement :</strong> en entretien, on demande
                    au candidat d'expliquer la différence entre <code className="rounded bg-white px-1 font-mono text-xs">re-rank</code>{' '}
                    et <code className="rounded bg-white px-1 font-mono text-xs">re-write</code>, ou
                    comment il gère le <code className="rounded bg-white px-1 font-mono text-xs">prompt versioning</code> en
                    prod. Ces 2 questions éliminent 60% des candidats qui
                    "font de l'IA" sans la maîtriser.
                  </p>
                </div>
              </div>
            </div>
            <div>
              <IaStackBadges />
            </div>
          </div>
        </div>
      </section>

      {/* MÉTIERS — grid des 13 métiers IA */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Métiers IA que nous recrutons
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              13 métiers IA. 13 expertises distinctes.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Chaque métier IA a sa propre stack, ses propres compétences,
              son propre marché salarial. Confondre un AI Engineer et un
              Data Scientist, c'est garantir un raté de recrutement. Voilà
              ceux qu'on sait reconnaître et recruter.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {METIERS.map((m) => {
              const Icon = m.icon;
              return (
                <Link
                  key={m.slug}
                  to={`/ia-paca/metiers/${m.slug}`}
                  className="group relative flex flex-col rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md"
                >
                  {m.isNew && (
                    <span className="absolute -top-2 right-4 rounded-full bg-violet-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                      Nouveau 2026
                    </span>
                  )}
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-base font-bold text-slate-900 group-hover:text-violet-700">
                    {m.label}
                  </h3>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {m.pitch}
                  </p>
                  <div className="mt-3 border-t border-slate-100 pt-3 text-[11px] text-slate-500">
                    Salaire PACA :{' '}
                    <strong className="text-slate-900 tabular-nums">{m.salary}</strong>
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-violet-700">
                    Voir la fiche
                    <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SALAIRES — preuve marché */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Salaires IA en PACA — mars 2026
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Les fourchettes réelles. Pas les théoriques.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Compilation de nos observations terrain + données publiques
              récentes (Hellowork, Glassdoor, Indeed, Factoriel). Ajustement
              régional : -10 à -20% vs Paris, +5% sur Sophia Antipolis
              (tension forte sur ML/AI Engineer).
            </p>
          </div>
          <div className="mt-8">
            <IaSalaryTable />
          </div>
          <div className="mt-6 rounded-xl border border-pink-200 bg-pink-50/50 p-5">
            <div className="flex items-start gap-3">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-pink-600" />
              <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-900">
                  Baromètre Salaires IA PACA 2026 (PDF complet)
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Version détaillée 25 pages : métier × ville × séniorité ×
                  stack, primes, packages, comparaison Paris. Mis à jour
                  chaque trimestre. <strong>Gratuit, donnée réelle.</strong>
                </p>
                <Link
                  to="/ia-paca/barometre-salaires-ia-paca-2026"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-pink-700 hover:underline"
                >
                  Recevoir le baromètre par email
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VILLES — pages géo */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Où on intervient
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              PACA, le sud techno de la France.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Pôle Alpha inauguré début 2026, 70 M€ de plan régional IA,
              Mistral AI × CMA CGM 100 M€, Sophia Antipolis 2 500 boîtes
              tech, French Tech Aix-Marseille active. Le sud est en train de
              prendre sa place dans l'IA française. Nous sommes au cœur.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {VILLES.map((v) => (
              <Link
                key={v.slug}
                to={`/ia-paca/${v.slug}`}
                className="group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-pink-300 hover:shadow-md"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-pink-50 text-pink-600">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-pink-700">
                      {v.label}
                    </h3>
                    <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600">
                      {v.tag}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    {v.pitch}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* NOTRE MÉTHODE — anti-bullshit */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Notre méthode
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              5 étapes. Aucune n'est de la pub.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              On ne vous promet pas un "expert sous 48h". On vous propose une
              méthode qui marche réellement quand le poste est technique et
              le marché tendu.
            </p>
          </div>

          <ol className="mt-12 space-y-6">
            {[
              {
                num: '01',
                title: 'Brief technique avec votre CTO ou Head of Data',
                body: "Pas avec un RH. On discute architecture, stack, contraintes prod, dette technique IA. 30-60 min. Si on n'est pas alignés techniquement, on vous le dit dans la première heure.",
                time: '30-60 min',
              },
              {
                num: '02',
                title: 'Sourcing ciblé sur les bons canaux',
                body: "Pas de LinkedIn shotgun. On combine : notre vivier qualifié PACA, communautés tech locales (Devfest Riviera, IA Marseille, French Tech), GitHub, Hugging Face, papers récents. On vise les 50 bons profils, pas 500 tièdes.",
                time: '5-10 jours',
              },
              {
                num: '03',
                title: 'Filtrage technique par notre équipe IA',
                body: "Cas pratique réel : RAG sur un dataset fourni, debug d'un agent qui hallucine, revue d'eval LLM. Pas de quiz QCM. On élimine 60% des candidats qui parlent bien mais ne savent pas faire.",
                time: '2-3 jours',
              },
              {
                num: '04',
                title: 'Présentation 3-5 short-list + rapport technique honnête',
                body: "Pour chaque candidat : ses vrais forts/faibles techniques, son niveau réel sur votre stack, son delta avec vos attentes. Pas de blabla commercial. Si on a un doute, on le dit.",
                time: '7-14 jours',
              },
              {
                num: '05',
                title: 'Suivi post-embauche 90 jours + garantie',
                body: "On reste en lien avec votre nouvel embauché et vous pendant 90 jours. Si ça ne colle pas (départ ou échec), on relance le sourcing gratuitement.",
                time: '90 jours',
              },
            ].map((step) => (
              <li
                key={step.num}
                className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 sm:flex-row sm:items-start"
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-pink-100 to-violet-100 font-mono text-base font-bold text-violet-700">
                  {step.num}
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">
                      {step.title}
                    </h3>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                      {step.time}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* POSTURE — transparence sur les premiers placements */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/40 p-8 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-amber-700">
                  Transparence
                </div>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">
                  Nos premiers placements IA structurés démarrent ce trimestre.
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-700">
                  Recruteurs.io est un cabinet de recrutement actif depuis
                  plusieurs années en PACA. La verticale IA — avec son
                  positionnement praticien, sa méthode de filtrage technique
                  et son baromètre marché — est en plein démarrage. Nous
                  préférons vous le dire honnêtement plutôt que d'inventer des
                  chiffres comme la majorité des cabinets le font.
                </p>
                <p className="mt-3 text-base leading-relaxed text-slate-700">
                  <strong>Ce que vous obtenez en travaillant avec nous
                  aujourd'hui :</strong> une équipe qui pratique l'IA, une
                  méthode de filtrage rigoureuse, un accès à la donnée marché
                  PACA, et la même attention que celle qu'on porterait à
                  notre propre recrutement. Pas une usine, pas un commercial,
                  pas un "expert" auto-proclamé.
                </p>
                <Link
                  to="/reservation"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 hover:underline"
                >
                  Discutons de votre besoin sans engagement
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Outils & analyses — lead magnets + vérités inconfortables */}
      <section className="bg-white py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              Outils & analyses
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Ce qui se passe vraiment sur le marché IA en PACA.
            </h2>
            <p className="mt-4 text-base text-slate-600">
              On publie ce qu'on observe sur le terrain. Pas du contenu
              générique. Des outils concrets (baromètre, quiz de maturité)
              et des analyses qui dérangent les conventions du secteur.
            </p>
          </div>

          {/* Lead magnets (priorité) */}
          <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
            <Link
              to="/ia-paca/carte-entreprises"
              className="group flex flex-col rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30 p-6 transition hover:border-emerald-400 hover:shadow-md"
            >
              <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-300 bg-emerald-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                <MapPin className="h-3 w-3" />
                Outil cartographique
              </div>
              <h3 className="mt-4 text-lg font-bold leading-snug text-slate-900 group-hover:text-emerald-700">
                Carte interactive des entreprises IA en PACA
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                35 acteurs cartographiés (corporates, scale-ups, startups,
                labos, hubs) sur Marseille, Aix, Sophia, Nice, Toulon.
                Filtres par ville et par catégorie.
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                Explorer la carte
                <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </div>
            </Link>

            <Link
              to="/ia-paca/barometre-salaires-ia-paca-2026"
              className="group flex flex-col rounded-2xl border-2 border-violet-200 bg-gradient-to-br from-white to-violet-50/30 p-6 transition hover:border-violet-400 hover:shadow-md"
            >
              <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-violet-300 bg-violet-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-700">
                <Sparkles className="h-3 w-3" />
                Étude exclusive · mars 2026
              </div>
              <h3 className="mt-4 text-lg font-bold leading-snug text-slate-900 group-hover:text-violet-700">
                Baromètre salaires IA en PACA — édition 2026
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                13 métiers · 4 villes · 4 séniorités. Compilation de 150+
                entreprises et 250+ candidats. PDF imprimable envoyé par
                email + mises à jour trimestrielles.
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-violet-700">
                Recevoir le baromètre
                <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </div>
            </Link>

            <Link
              to="/ia-paca/quiz-ai-readiness"
              className="group flex flex-col rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-white to-orange-50/30 p-6 transition hover:border-orange-400 hover:shadow-md"
            >
              <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-orange-300 bg-orange-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-700">
                <Sparkles className="h-3 w-3" />
                Outil interactif · 5 min
              </div>
              <h3 className="mt-4 text-lg font-bold leading-snug text-slate-900 group-hover:text-orange-700">
                Quiz : votre AI Readiness Score
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                15 questions sur 5 dimensions (stratégie, données, équipe,
                infra, gouvernance). Profil de maturité + 3 actions
                prioritaires à 90 jours, rapport personnalisé par email.
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-orange-700">
                Démarrer le quiz
                <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          </div>

          {/* Article-canon */}
          <div className="mt-5">
            <Link
              to="/ia-paca/articles/ai-engineers-rag-production"
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-pink-300 hover:shadow-md"
            >
              <div className="inline-flex w-fit items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-700">
                <AlertTriangle className="h-3 w-3" />
                Vérité inconfortable
              </div>
              <h3 className="mt-4 text-lg font-bold leading-snug text-slate-900 group-hover:text-pink-700">
                80% des "AI Engineers" PACA ne savent pas faire un RAG en production
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Enquête sur 6 mois et 80+ candidats qualifiés. Pourquoi le
                titre "AI Engineer" est devenu un attrape-CV, comment
                qualifier les vrais profils, et ce qu'un mauvais
                recrutement coûte vraiment.
              </p>
              <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-pink-700">
                Lire l'enquête
                <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ premium */}
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-4xl px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
              FAQ
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Les vraies questions des CTO et fondateurs.
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQ.map((q) => (
              <details
                key={q.question}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-slate-300"
              >
                <summary className="flex cursor-pointer items-start justify-between gap-4 text-base font-semibold text-slate-900 [&::-webkit-details-marker]:hidden">
                  <span>{q.question}</span>
                  <span className="mt-0.5 text-slate-400 transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <div className="mt-4 text-sm leading-relaxed text-slate-600">
                  {q.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <IaPacaCTA />
    </Layout>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function Pillar({
  Icon,
  title,
  body,
  tag,
}: {
  Icon: typeof Wrench;
  title: string;
  body: string;
  tag: string;
}) {
  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white p-6 transition hover:border-violet-300 hover:shadow-md">
      <div className="absolute right-4 top-4 rounded-full bg-violet-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-violet-700">
        {tag}
      </div>
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-pink-100 to-violet-100 text-violet-700">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="mt-4 text-base font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

const FAQ = [
  {
    question: "Pourquoi 90% des recrutements IA en France échouent-ils ?",
    answer:
      "Trois raisons principales : (1) le poste a été défini par un RH sans validation technique, (2) les candidats ont été filtrés sur des mots-clés CV au lieu de tests pratiques, (3) le cabinet n'a pas su distinguer un Data Scientist d'un AI Engineer. Conséquence : embauche d'un profil mal aligné, départ ou échec en moins de 12 mois.",
  },
  {
    question:
      "Quelle est concrètement la différence entre un AI Engineer et un Data Scientist ?",
    answer:
      "Un Data Scientist explore les données et construit des modèles statistiques ou ML — souvent en notebooks, focus exploration et insight. Un AI Engineer prend ces modèles (ou des LLMs existants) et les met en production : APIs, RAG, agents, monitoring, scalabilité. Le premier raisonne en cellule Jupyter, le second en pull request. Pour un projet RAG en prod, vous voulez un AI Engineer. Pour un projet de prédiction de churn, vous voulez probablement un Data Scientist.",
  },
  {
    question:
      "Combien de temps prend en moyenne un recrutement IA en PACA ?",
    answer:
      "Pour un AI Engineer Senior : 30 à 60 jours de bout en bout (brief → signature). Pour un MLOps senior : 45 à 90 jours (très tendu sur le marché). Pour un Data Scientist confirmé : 25 à 45 jours. Ces délais supposent un brief clair et un process décisionnel rapide chez vous. Si votre process intègre 4 entretiens + test technique + comité, ajoutez 2-3 semaines.",
  },
  {
    question:
      "Comment vérifiez-vous qu'un candidat sait vraiment faire de l'IA en prod ?",
    answer:
      "Nous demandons systématiquement : (1) un cas pratique de RAG sur un dataset fourni — chunking, retrieval, eval ; (2) une revue de prompt qu'il a versionné en prod et expliquer son test set ; (3) un débogue d'agent qui hallucine ou boucle. Ces 3 exercices éliminent environ 60% des candidats qui ont le bon CV mais pas la pratique terrain.",
  },
  {
    question: "Quels secteurs en PACA recrutent le plus en IA actuellement ?",
    answer:
      "Top 5 secteurs PACA actifs en IA 2026 : (1) maritime / logistique (CMA CGM Track Genius, Mistral AI partnership), (2) DeepTech / robotique (Marseille, Aix), (3) FinTech (Nice, Monaco), (4) santé / biotech (Sophia, Marseille), (5) défense / naval (Toulon). Les PME en transformation IA générative explosent partout, secteur indépendant.",
  },
  {
    question: "Avez-vous des profils déjà placés que nous pouvons contacter ?",
    answer:
      "Nous démarrons cette verticale ce trimestre — nous préférons être transparents là-dessus plutôt que d'inventer des références. En revanche, nous opérons nos propres systèmes IA en production depuis 2022 (agents, workflows, ATS propriétaire) — vous pouvez voir ce qu'on construit en discutant 30 min avec notre équipe. La meilleure référence d'un cabinet IA, c'est ce qu'il sait construire lui-même.",
  },
  {
    question: "Quels sont vos honoraires pour un recrutement IA ?",
    answer:
      "Modèle au succès : un pourcentage du salaire brut annuel (typiquement 18-22% selon la rareté du profil), facturable en 2 fois (50% à la signature, 50% à la fin de période d'essai). Garantie remplacement gratuit si rupture pendant la période d'essai. Brief initial et short-list : gratuits.",
  },
  {
    question: "Recrutez-vous uniquement en CDI ou aussi des freelances IA ?",
    answer:
      "Notre cœur de métier est le CDI (le profil IA s'investit mieux quand il est intégré). Pour les freelances, nous avons un réseau qualifié de consultants IA seniors PACA — mais nous n'avons pas la prétention d'être une plateforme freelance. Nous mettons en relation sans intermédiation lourde si c'est ce dont vous avez besoin.",
  },
];
